import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import CabCrudPage from './CabCrudPage';
import useCabLookups from './useCabLookups';
import {
  createTransportPackageMedia,
  deleteTransportPackageMedia,
  listTransportPackageMedia,
  setTransportPackageMediaCover,
  uploadAdminImage,
} from '../../../api/cabsAdmin';

const AdminCabsTransportPackages = () => {
  const { locations } = useCabLookups();
  const [locationFilter, setLocationFilter] = useState('');
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [activePackage, setActivePackage] = useState(null);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mediaError, setMediaError] = useState('');

  const loadMedia = async (packageId) => {
    setMediaError('');
    try {
      const res = await listTransportPackageMedia(packageId);
      setMedia(res.data || res || []);
    } catch (err) {
      setMediaError(err.message || 'Failed to load media');
    }
  };

  useEffect(() => {
    if (activePackage?.id) {
      loadMedia(activePackage.id);
    }
  }, [activePackage?.id]);

  const openMedia = (pkg) => {
    setActivePackage(pkg);
    setMediaModalOpen(true);
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activePackage?.id) return;
    setUploading(true);
    setMediaError('');
    try {
      const uploaded = await uploadAdminImage(file);
      await createTransportPackageMedia(activePackage.id, {
        url: uploaded?.path || uploaded?.url || uploaded?.filePath || uploaded?.data?.path || '',
        altText: file.name,
        isCover: media.length === 0,
        sortOrder: media.length,
      });
      await loadMedia(activePackage.id);
    } catch (err) {
      setMediaError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (mediaId) => {
    if (!activePackage?.id) return;
    setMediaError('');
    try {
      await deleteTransportPackageMedia(mediaId);
      await loadMedia(activePackage.id);
    } catch (err) {
      setMediaError(err.message || 'Failed to delete media');
    }
  };

  const handleSetCover = async (mediaId) => {
    if (!activePackage?.id) return;
    setMediaError('');
    try {
      await setTransportPackageMediaCover(mediaId, true);
      await loadMedia(activePackage.id);
    } catch (err) {
      setMediaError(err.message || 'Failed to update cover');
    }
  };

  const locationOptions = useMemo(
    () => locations.map((l) => ({ value: l.id, label: `${l.name} (${l.city})` })),
    [locations]
  );

  const mapItemToForm = (item, defaultItem) => ({
    ...defaultItem,
    ...item,
    itineraryJson: item?.itineraryJson ? JSON.stringify(item.itineraryJson, null, 2) : '',
    galleryJson: item?.galleryJson ? JSON.stringify(item.galleryJson, null, 2) : '',
  });

  const transformPayload = (payload) => {
    let itineraryJson = null;
    let galleryJson = null;
    if (payload.itineraryJson && String(payload.itineraryJson).trim() !== '') {
      try {
        itineraryJson = JSON.parse(payload.itineraryJson);
      } catch (err) {
        throw new Error('Itinerary JSON is invalid.');
      }
    }
    if (payload.galleryJson && String(payload.galleryJson).trim() !== '') {
      try {
        galleryJson = JSON.parse(payload.galleryJson);
      } catch (err) {
        throw new Error('Gallery JSON is invalid.');
      }
    }
    return {
      ...payload,
      days: Number(payload.days),
      nights: Number(payload.nights),
      durationDays: payload.durationDays === '' ? null : Number(payload.durationDays),
      itineraryJson,
      galleryJson,
      sortOrder: payload.sortOrder === '' ? 0 : Number(payload.sortOrder),
    };
  };

  return (
    <>
      <CabCrudPage
        title="Transport Packages"
        description="Build transport package offerings and details."
        resource="transport-packages"
        uploadHandler={uploadAdminImage}
        listParams={() => ({ locationId: locationFilter || undefined })}
        listParamsDeps={[locationFilter]}
        mapItemToForm={mapItemToForm}
        defaultItem={{
          title: '',
          slug: '',
          locationId: '',
          days: '',
          nights: '',
          durationDays: '',
          coverImageUrl: '',
          description: '',
          highlights: '',
          inclusions: '',
          exclusions: '',
          itineraryJson: '',
          galleryJson: '',
          terms: '',
          sortOrder: 0,
          isActive: true,
        }}
        filterControls={(
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label text-sm">Filter by location</label>
              <select
                className="form-select"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="">All locations</option>
                {locationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
        columns={[
          { key: 'title', label: 'Title' },
          { key: 'slug', label: 'Slug' },
          {
            key: 'location',
            label: 'Location',
            render: (row) => row.location?.name || '-',
          },
          { key: 'days', label: 'Days' },
          { key: 'nights', label: 'Nights' },
          { key: 'sortOrder', label: 'Sort' },
        ]}
        fields={[
          { name: 'title', label: 'Title', required: true },
          { name: 'slug', label: 'Slug', required: true },
          { name: 'locationId', label: 'Location', type: 'select', required: true, options: locationOptions },
          { name: 'days', label: 'Days', type: 'number', required: true },
          { name: 'nights', label: 'Nights', type: 'number', required: true },
          { name: 'durationDays', label: 'Display duration', type: 'number' },
          { name: 'coverImageUrl', label: 'Cover image', type: 'image', col: 'col-12' },
          { name: 'sortOrder', label: 'Sort order', type: 'number' },
          { name: 'description', label: 'Description', type: 'textarea', col: 'col-12', rows: 3 },
          { name: 'highlights', label: 'Highlights', type: 'textarea', col: 'col-12', rows: 3 },
          { name: 'inclusions', label: 'Inclusions', type: 'textarea', col: 'col-12', rows: 3 },
          { name: 'exclusions', label: 'Exclusions', type: 'textarea', col: 'col-12', rows: 3 },
          { name: 'itineraryJson', label: 'Itinerary JSON', type: 'textarea', col: 'col-12', rows: 6 },
          { name: 'galleryJson', label: 'Gallery JSON', type: 'textarea', col: 'col-12', rows: 4 },
          { name: 'terms', label: 'Terms', type: 'textarea', col: 'col-12', rows: 3 },
          { name: 'isActive', label: 'Active', type: 'checkbox', col: 'col-md-6' },
        ]}
        rowActions={(item) => (
          <button className="btn btn-sm btn-outline-dark" onClick={() => openMedia(item)}>
            Media
          </button>
        )}
        transformPayload={transformPayload}
      />

      <Modal show={mediaModalOpen} onHide={() => setMediaModalOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Package Media - {activePackage?.title || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mediaError && <div className="alert alert-danger">{mediaError}</div>}
          <div className="mb-3">
            <input className="form-control" type="file" accept="image/*" onChange={handleUpload} disabled={uploading} />
            <div className="form-text">Upload a cover or gallery image.</div>
          </div>
          <div className="row g-3">
            {media.length === 0 && <div className="text-muted">No media yet.</div>}
            {media.map((item) => (
              <div className="col-md-4" key={item.id}>
                <div className="border rounded-lg p-2">
                  <img src={item.url} alt={item.altText || 'Package'} className="w-100 rounded" />
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleSetCover(item.id)}>
                      {item.isCover ? 'Cover' : 'Set cover'}
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(item.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={() => setMediaModalOpen(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminCabsTransportPackages;
